import type {
  PrinterConnectionConfig,
  PrinterState,
  PrinterTelemetry,
  PrinterCapabilities,
  PrinterEvent,
  PrinterEventType,
  CameraStream,
  IPrinterConnection,
  ConnectionStatus,
} from '../printerConnection.types'

export abstract class BasePrinterAdapter implements IPrinterConnection {
  protected config: PrinterConnectionConfig
  protected state: PrinterState
  protected eventListeners: Set<(event: PrinterEvent) => void> = new Set()
  protected pollTimer: ReturnType<typeof setInterval> | null = null
  protected reconnectTimer: ReturnType<typeof setTimeout> | null = null
  protected reconnectAttempts = 0
  protected maxReconnectAttempts = 5

  constructor(config: PrinterConnectionConfig) {
    this.config = config
    this.state = {
      id: config.id,
      config,
      connectionStatus: 'disconnected',
      printerStatus: 'offline',
    }
  }

  abstract connect(): Promise<void>
  abstract disconnect(): Promise<void>
  abstract getTelemetry(): Promise<PrinterTelemetry>
  abstract getCapabilities(): Promise<PrinterCapabilities>
  abstract getCameraStream(): Promise<CameraStream | null>
  abstract startPrint(filename: string): Promise<void>
  abstract pausePrint(): Promise<void>
  abstract resumePrint(): Promise<void>
  abstract cancelPrint(): Promise<void>
  abstract setNozzleTemp(temp: number): Promise<void>
  abstract setBedTemp(temp: number): Promise<void>
  abstract homeAxes(axes?: ('x' | 'y' | 'z')[]): Promise<void>
  abstract jogAxis(axis: 'x' | 'y' | 'z', distance: number): Promise<void>
  abstract listFiles(): Promise<string[]>
  abstract uploadFile(file: File, filename?: string): Promise<void>
  abstract deleteFile(filename: string): Promise<void>

  getState(): PrinterState {
    return { ...this.state }
  }

  protected setConnectionStatus(status: ConnectionStatus): void {
    this.state.connectionStatus = status
    this.emitEvent(status === 'connected' ? 'connected' : 'disconnected')
  }

  protected updateTelemetry(telemetry: PrinterTelemetry): void {
    this.state.telemetry = telemetry
    this.state.lastSeen = new Date()
    this.emitEvent('telemetry', telemetry)
  }

  protected emitEvent(type: PrinterEventType, data?: unknown): void {
    const event: PrinterEvent = {
      type,
      printerId: this.config.id,
      timestamp: new Date(),
      data,
    }
    this.eventListeners.forEach((listener) => listener(event))
  }

  onEvent(callback: (event: PrinterEvent) => void): () => void {
    this.eventListeners.add(callback)
    return () => this.eventListeners.delete(callback)
  }

  protected startPolling(): void {
    if (this.pollTimer) return
    const interval = this.config.pollInterval || 2000
    this.pollTimer = setInterval(async () => {
      try {
        const telemetry = await this.getTelemetry()
        this.updateTelemetry(telemetry)
      } catch (error) {
        console.error(`Polling error for ${this.config.name}:`, error)
        if (this.config.autoReconnect) {
          this.handleDisconnect()
        }
      }
    }, interval)
  }

  protected stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  protected async handleDisconnect(): Promise<void> {
    this.stopPolling()
    this.setConnectionStatus('reconnecting')

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000)
      this.reconnectAttempts++
      
      this.reconnectTimer = setTimeout(async () => {
        try {
          await this.connect()
          this.reconnectAttempts = 0
        } catch {
          this.handleDisconnect()
        }
      }, delay)
    } else {
      this.setConnectionStatus('error')
      this.state.lastError = 'Max reconnection attempts reached'
      this.emitEvent('error', { message: 'Max reconnection attempts reached' })
    }
  }

  protected buildUrl(path: string): string {
    const protocol = this.config.useTls ? 'https' : 'http'
    const port = this.config.port ? `:${this.config.port}` : ''
    return `${protocol}://${this.config.host}${port}${path}`
  }

  protected buildWsUrl(path: string): string {
    const protocol = this.config.useTls ? 'wss' : 'ws'
    const port = this.config.port ? `:${this.config.port}` : ''
    return `${protocol}://${this.config.host}${port}${path}`
  }
}
